// Cloud deployment - Updated cache buster
import type { InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { auth } from '../firebase';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  params: {
    _t: Date.now() // Cache buster
  }
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // Log the request URL for debugging
    console.log('Making request to:', `${config.baseURL || ''}${config.url || ''}`);

    // Always get the latest token from Firebase Auth
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Fallback for local dev: use token from localStorage if present
      const token = localStorage.getItem('firebaseToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add cache buster to all requests
    config.params = {
      ...config.params,
      _t: Date.now()
    };

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401/403 and we haven't tried to refresh the token yet
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Get current user
        const user = auth.currentUser;
        if (user) {
          // Force token refresh
          const newToken = await user.getIdToken(true);
          localStorage.setItem('firebaseToken', newToken);

          // Update the failed request's authorization header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        // If token refresh fails, redirect to login
        localStorage.removeItem('firebaseToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export interface ExportRequest {
  customer_info: {
    name: string;
    organization: string;
    email: string;
  };
  form_data: Record<string, any>;
  configurations: Array<Record<string, any>>;
}

export const exportData = async (data: ExportRequest) => {
  const response = await api.post('/api/export', data, {
    responseType: 'blob'
  });
  return response.data;
};

export interface CalculateRequest {
  analyticsEngine: string;
  fullAsset: string;
  onDemand: number;
  realTime: number;
  research: number;
  videoComplexity: string;
  resolution: string;
}

export const calculateHardware = async (data: CalculateRequest) => {
  const response = await api.post('/api/calculate', data);
  return response.data;
};

export const uploadExcel = async (formData: FormData) => {
  try {
    const response = await api.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error during upload:', error);
    throw error;
  }
};

export interface User {
  uid: string;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  disabled: boolean;
  last_sign_in_time: number | null;
  creation_time: number;
  is_admin: boolean;
  custom_claims: Record<string, any>;
}

export const getUsers = async (): Promise<User[]> => {
  try {
    const response = await api.get('/api/users');
    return response.data.users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const setUserAdminStatus = async (userId: string, isAdmin: boolean): Promise<any> => {
  try {
    const response = await api.post(`/api/users/${userId}/set-admin`, null, {
      params: { is_admin: isAdmin }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating admin status:', error);
    throw error;
  }
};

export const setUserDisabledStatus = async (userId: string, disabled: boolean): Promise<any> => {
  try {
    const response = await api.post(`/api/users/${userId}/disable`, null, {
      params: { disabled }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

export const deleteUser = async (userId: string): Promise<any> => {
  try {
    console.log(`🔍 Attempting to delete user: ${userId}`);
    const response = await api.delete(`/api/users/${userId}`);
    console.log(`✅ Successfully deleted user: ${userId}`);
    return response.data;
  } catch (error: any) {
    console.error(`❌ Error deleting user ${userId}:`, error);
    
    // Log detailed error information
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request was made but no response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    
    throw error;
  }
};

export const createUser = async (email: string, password: string, isAdmin: boolean = false): Promise<any> => {
  try {
    console.log('Creating user with data:', { email, password: '***', is_admin: isAdmin });
    const response = await api.post('/api/users/create', {
      email,
      password,
      is_admin: isAdmin
    });
    return response.data;
  } catch (error: any) {
    console.error('Error creating user:', error);
    
    // Log detailed error information
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request was made but no response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    
    throw error;
  }
};

export default api; 